// src\themes\tolica\elements\Header\HeaderNavMenu.tsx
"use client"

import type { MenuItem, MenuChildItem } from "@/core/types/menuApi"
import Link from "next/link"
import Image from "next/image"
import { useState, useRef, useEffect } from "react"
import { useDictionary } from "@/core/hooks/use-dictionary"
import { useHeaderScrolled } from "@/core/hooks/use-header-scrolled"
import { useMegaMenuStore } from "@/stores/megaMenuStore"
import { cn, normalizeLink } from "@/core/lib/utils"

interface HeaderNavMenuProps {
  mainMenu: MenuItem[]
}

export default function HeaderNavMenu({ mainMenu }: HeaderNavMenuProps) {
  const [activeItem, setActiveItem] = useState<MenuItem | null>(null)
  const [hoveredChild, setHoveredChild] = useState<MenuChildItem | MenuItem | null>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const { dictionary } = useDictionary()
  const scrolled = useHeaderScrolled()
  // با باز شدن مگامنو، HeaderShell هم باید همزمان به حالت جامد بره تا
  // نوار هدر و پنل کشویی زیرش یکدست دیده بشن (نه هدر شفاف روی پنل مشکی)
  const setMegaMenuOpen = useMegaMenuStore(s => s.setIsOpen)

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
      setMegaMenuOpen(false)
    }
  }, [setMegaMenuOpen])

  const handleMouseEnter = (item: MenuItem) => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)

    if (item.children && item.children.length > 0) {
      setActiveItem(item)
      setHoveredChild(null)
      setIsMenuOpen(true)
      setMegaMenuOpen(true)
    } else {
      setIsMenuOpen(false)
      setActiveItem(null)
      setHoveredChild(null)
      setMegaMenuOpen(false)
    }
  }

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsMenuOpen(false)
      setMegaMenuOpen(false)
      setTimeout(() => {
        setActiveItem(null)
        setHoveredChild(null)
      }, 200)
    }, 80)
  }

  return (
    <nav
      className="hidden lg:flex items-center gap-7 text-sm"
      onMouseLeave={handleMouseLeave}
    >
      {mainMenu.map((item) => {
        const hasChildren = item.children && item.children.length > 0
        const isActive = activeItem?.id === item.id

        return (
          <div
            key={item.id}
            className="relative"
            onMouseEnter={() => handleMouseEnter(item)}
          >
            {hasChildren ? (
              <Link
                href={normalizeLink(item.link)}
                className={`
                  py-2 text-sm
                  transition-all duration-200 outline-none
                  relative group block
                  ${isActive ? "text-primary-0" : "text-primary-100 hover:text-primary-0"}
                `}
              >
                {item.title}
                <span className={`
                  absolute bottom-0 rtl:right-0 ltr:left-0 h-px bg-primary-0
                  transition-all duration-300 ease-out
                  ${isActive ? 'w-full' : 'w-0 group-hover:w-full'}
                `} />
              </Link>
            ) : (
              <Link
                href={normalizeLink(item.link)}
                className="py-2 text-sm text-primary-100 hover:text-primary-0 transition-all duration-200 block relative group"
              >
                {item.title}
                <span className={`
                  absolute bottom-0 rtl:right-0 ltr:left-0 h-px bg-primary-0
                  transition-all duration-300 ease-out
                  ${isActive ? 'w-full' : 'w-0 group-hover:w-full'}
                `} />
              </Link>
            )}
          </div>
        )
      })}

      {activeItem && (
        <>
          <div
            className={cn("fixed left-0 right-0 z-50 w-full transition-all duration-200 ease-out",
              isMenuOpen
                ? "opacity-100 pointer-events-auto translate-y-0"
                : "opacity-0 pointer-events-none -translate-y-2", scrolled ? "top-14" : "top-20")
            }
            style={{ transformOrigin: 'top' }}
            onMouseEnter={() => {
              if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
            }}
            onMouseLeave={handleMouseLeave}
          >
            <div className="w-full bg-neutral-950/90 border-t border-white/10 shadow-2xl">
              <div className="px-8 py-7">
                <div className="flex gap-0">
                  <div className="flex flex-wrap gap-x-16 gap-y-6 flex-1">
                    {activeItem.children!.map((child, index) => {
                      const hasGrandchildren = child.children && child.children.length > 0

                      return (
                        <div
                          key={child.id}
                          className="flex flex-col min-w-35 animate-in fade-in"
                          style={{
                            animationDelay: `${index * 40}ms`,
                            animationDuration: '300ms',
                            animationFillMode: 'backwards'
                          }}
                        >
                          <Link
                            href={normalizeLink(child.link)}
                            onMouseEnter={() => setHoveredChild(child)}
                            className="text-xs font-semibold text-white mb-2.5 hover:text-primary-400 transition-colors duration-200 relative group inline-block w-fit"
                          >
                            {child.title}
                            <span className="absolute bottom-0 rtl:right-0 ltr:left-0 w-0 h-px bg-primary-400 group-hover:w-full transition-all duration-300" />
                          </Link>

                          {hasGrandchildren && (
                            <ul className="flex flex-col gap-0">
                              {child.children!.map((grandchild) => (
                                <li key={grandchild.id}>
                                  <Link
                                    href={normalizeLink(grandchild.link)}
                                    onMouseEnter={() => setHoveredChild(grandchild)}
                                    className="text-sm text-white/50 hover:text-white rtl:hover:-translate-x-1  ltr:hover:translate-x-1 py-1.5 block transition-all duration-200"
                                  >
                                    {grandchild.title}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )
                    })}
                  </div>

                  {hoveredChild?.imageUrl && (
                    <div
                      className="hidden xl:flex flex-col shrink-0 w-[420px] mr-4 self-center"
                      onMouseEnter={() => setHoveredChild(hoveredChild)}
                    >
                      <div className="relative w-full aspect-[3/2] overflow-hidden bg-neutral-900">
                        <Image
                          key={hoveredChild.imageUrl}
                          src={hoveredChild.imageUrl}
                          alt={hoveredChild.title}
                          fill
                          sizes="420px"
                          className="object-cover transition-transform duration-700"
                          priority
                        />
                      </div>
                    </div>
                  )}

                  <div
                    className="flex flex-col shrink-0 border-r border-white/10 pr-8 mr-4 min-w-27.5"
                    onMouseEnter={() => setHoveredChild(null)}
                  >
                    <Link
                      href={normalizeLink(activeItem.link)}
                      className="text-xs font-semibold text-white mb-2.5 hover:text-primary-400 transition-colors duration-200 relative group inline-block w-fit"
                    >
                      {activeItem.title}
                      <span className="absolute bottom-0 rtl:right-0 ltr:left-0 w-0 h-px bg-primary-400 group-hover:w-full transition-all duration-300" />
                    </Link>
                    <Link
                      href={normalizeLink(activeItem.link)}
                      className="text-sm text-white/50 hover:text-white hover:translate-x-1 py-1.5 transition-all duration-200"
                    >
                      {dictionary.ui.pages.seeAll}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </nav>
  )
}